import {QueryService} from './query.service';
import {Observable, of, switchMap} from 'rxjs';
import {EntityStoreState} from '../models/entity-store-state.model';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {SortOrder} from '../enums/sort-order.enum';
import {HashMap} from '../models/hash-map.model';
import {EntityStoreConfig} from '../models/entity-store-config.model';
import {StoreService} from './store.service';
import {distinctUntilChangedArray} from '../../../util/rxjs-customs/distinct-until-changed-array.util';

export class EntityQueryService<StateType extends EntityStoreState<EntityType>, EntityType> extends QueryService<StateType> {
    protected config: EntityStoreConfig<EntityType>;

    public constructor(store: StoreService<StateType>, config: EntityStoreConfig<EntityType>) {
        super(store);
        this.config = config;
    }

    public selectAll(): Observable<Array<EntityType>> {
        return this.select((state: StateType) => state.entities)
            .pipe(map(() => this.getAll()));
    }

    public getAll(): Array<EntityType> {
        return this.entitiesToArray(this.getValue());
    }

    public hasEntity(id: string): boolean {
        if (id === null || id === undefined) {
            return this.getValue().ids.length > 0;
        }

        return id in this.getValue().entities;
    }

    public getEntity(id: string): EntityType | undefined {
        return this.getValue().entities[id];
    }

    public getFirstActive(): EntityType | undefined {
        const activeId = this.getFirstActiveId();
        if (activeId) {
            return this.getEntity(activeId);
        }
        return undefined;
    }

    public getFirstActiveId(): string | undefined {
        return this.getValue().active[0];
    }

    public getActives(): Array<string> {
        return this.getValue().active;
    }

    public getCount(): number {
        return this.getValue().ids.length;
    }

    public selectActiveIds(): Observable<Array<string>> {
        return this.select<Array<string>>((state: StateType) => state.active);
    }

    public selectFirstActiveId(): Observable<string | undefined> {
        return this.selectActiveIds()
            .pipe(map(ids => ids[0]));
    }

    public selectActives(): Observable<Array<EntityType>> {
        return this.selectActiveIds()
            .pipe(switchMap(ids => this.selectMany(ids)));
    }

    public selectFirstActive(): Observable<EntityType | undefined> {
        return this.selectFirstActiveId()
            .pipe(switchMap(id => id ? this.selectEntity(id) : of(undefined)));
    }

    public selectLoading(): Observable<boolean> {
        return this.select(state => state.loading);
    }

    private selectMany(ids: Array<string>): Observable<Array<EntityType>> {
        if (!ids || !ids.length) {
            return of([]);
        }

        return this.select((state: StateType) => state.entities)
            .pipe(
                map((entities: HashMap<EntityType>) => {
                    return ids.reduce((selectedEntities, id: string) => {
                        if (entities[id] !== undefined) {
                            selectedEntities.push(entities[id]);
                        }
                        return selectedEntities;
                    }, [] as Array<EntityType>);
                }),
                distinctUntilChangedArray<EntityType>()
            );
    }

    private selectEntity(id: string): Observable<EntityType | undefined> {
        return this.select()
            .pipe(map(state => state.entities[id]), distinctUntilChanged());
    }

    private entitiesToArray(state: StateType): Array<EntityType> {
        let arr: Array<EntityType> = Object.values(state.entities);
        const sortByFnc = this.compareValues(this.config.sortBy, this.config.sortByOrder);
        arr = arr.sort((a, b) => sortByFnc(a, b));

        return arr;
    }

    private compareValues(key: keyof EntityType, order: SortOrder = SortOrder.ASC): (a: EntityType, b: EntityType) => number {
        return function (a: EntityType, b: EntityType): number {
            if (!a[key] || !b[key]) {
                return 0;
            }

            const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
            const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

            let comparison = 0;
            if (varA > varB) {
                comparison = 1;
            } else if (varA < varB) {
                comparison = -1;
            }
            return order == SortOrder.DESC ? comparison * -1 : comparison;
        };
    }
}
